import { useState, useEffect } from "react";
import { useEvent } from "../../../lib/hooks/useEvent";
import { useStencils } from "../../../lib/hooks/useStencils";
import { useStencilWeeks } from "../../../lib/hooks/useStencilWeeks";
import { updateStencilWeek } from "../../../lib/services/stencilWeekService";

const QuickUpdateModal = ({ onClose, refreshProgress, refreshStencilWeeks }) => {
  const { event, getEvent } = useEvent();
  const { stencils, getStencils } = useStencils();
  const { stencilWeeks, getStencilWeeks } = useStencilWeeks();

  const [popupSelectedStep, setPopupSelectedStep] = useState("printed");
  const [searchValue, setSearchValue] = useState("");
  const [popupStencil, setPopupStencil] = useState(null);
  const [popupTimeout, setPopupTimeout] = useState(null);

  const steps = ["printed", "cut", "traced", "carved"];

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    getEvent();
    getStencils();
    getStencilWeeks();
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const handlePopupStepClick = (step) => {
    setPopupSelectedStep(step);
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    const stencil = stencils.find((s) => s.id === searchValue.trim());

    if (stencil) {
      // Find all appearances of the inputted stencil
      const stencilWeekAppearances = stencilWeeks
        .filter((w) => w.stencil_id === searchValue.trim())
        .sort((a, b) => a.week_num - b.week_num);

      // Determine the specific week to update the status in
      let weekToUpdate = null;

      for (const week of stencilWeekAppearances) {
        if (week[popupSelectedStep] !== 3) {
          weekToUpdate = week;
          break;
        }
      }

      if (!weekToUpdate) {
        setSearchValue("");
        return;
      }

      // Set all previous steps to confirmed if they aren't already
      const stepIndex = steps.indexOf(popupSelectedStep);
      const weekNum = weekToUpdate.week_num;
      const newStepStatuses = {};

      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        if (i <= stepIndex) {
          newStepStatuses[step] = 3;
        } else {
          newStepStatuses[step] = weekToUpdate[step];
        }
      }

      const promise = updateStencilWeek(
        weekNum,
        stencil.id,
        newStepStatuses.printed,
        newStepStatuses.cut,
        newStepStatuses.traced,
        newStepStatuses.carved
      );

      promise.then((response) => {
        if (!response.ok) alert(`Failed to update selection for ${stencil.id}`);
        else {
          refreshProgress();
          refreshStencilWeeks();
        }
      });

      setPopupStencil({ ...stencil });
      setSearchValue("");

      if (popupTimeout) clearTimeout(popupTimeout);
      const timeout = setTimeout(() => setPopupStencil(null), 6000);
      setPopupTimeout(timeout);
    }
  };

  return (
    <div className="quick-update-overlay" onClick={onClose}>
      <div className="quick-update-card" onClick={(e) => e.stopPropagation()}>
        <div className="quick-update-header">
          <h3>Quick Update</h3>
          <button className="btn-close" onClick={onClose}>
            Back
          </button>
        </div>

        <div className="quick-update-content">
          <div className="quick-update-step-buttons">
            <p>Mark stencils as:</p>
            <button
              className={`step-button printed ${
                popupSelectedStep === "printed" ? "selected" : ""
              }`}
              id="printed"
              onClick={() => handlePopupStepClick("printed")}
            >
              Printed
            </button>
            <button
              className={`step-button cut ${
                popupSelectedStep === "cut" ? "selected" : ""
              }`}
              id="cut"
              onClick={() => handlePopupStepClick("cut")}
            >
              Cut
            </button>

            <button
              className={`step-button traced ${
                popupSelectedStep === "traced" ? "selected" : ""
              }`}
              id="traced"
              onClick={() => handlePopupStepClick("traced")}
            >
              Traced
            </button>
            <button
              className={`step-button carved ${
                popupSelectedStep === "carved" ? "selected" : ""
              }`}
              id="carved"
              onClick={() => handlePopupStepClick("carved")}
            >
              Carved
            </button>
          </div>
          <div className="quick-update-search">
            <p>Enter a Stencil ID or scan the barcode</p>
            <form onSubmit={handleSearchSubmit}>
              <input
                type="text"
                id="search-bar"
                placeholder="Stencil ID"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </form>
          </div>
          {popupStencil && (
            <div className="quick-update-popup">
              <div>
                <p>{popupStencil.id}</p>
                <p>{popupStencil.name}</p>
              </div>
              <img
                key={popupStencil.image_name}
                src={`/api/stencils/files/${popupStencil.image_name}`}
                alt={popupStencil.name}
                width={100}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickUpdateModal;
